import { useState } from "react";
import { APP_VERSION } from "@/constants";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  ChevronDownIcon,
  GitBranchIcon,
  SaveIcon,
  LoaderIcon,
  FileTextIcon,
  FileDiffIcon,
  RefreshCwIcon,
  LoaderCircleIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  ClockIcon,
  FileIcon,
  HistoryIcon,
  EyeIcon,
  EyeOffIcon,
  DownloadIcon,
} from "lucide-react";
import type { VersionEntry } from "@/lib/storage";

type ViewMode = "diff" | "pdf";

// Source for left editor (editing dropdown)
export type EditingSource = 
  | { type: "current-draft" }
  | { type: "default-template" }
  | { type: "version"; versionId: string };

// Source for right editor (compare dropdown)  
export type CompareSource = 
  | { type: "default-template" }
  | { type: "last-saved" }
  | { type: "version"; versionId: string };

interface TopBarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isCompiling: boolean;
  compileError: string | null;
  hasValidationErrors: boolean;
  onCompile: () => void | Promise<void>;
  onDownload?: () => void | Promise<void>;
  onSaveChanges?: () => void;
  onSaveNewVersion?: () => void;
  isSaving?: boolean;
  
  // Editing (left) dropdown
  editingSource: EditingSource;
  onEditingSourceChange?: (source: EditingSource) => void;
  
  // Compare (right) dropdown  
  compareSource: CompareSource;
  onCompareSourceChange?: (source: CompareSource) => void;
  
  // Template selector (for PDF view)
  selectedTemplate: string;
  onTemplateChange?: (template: string) => void;
  availableTemplates: string[];
  
  // Available versions for dropdowns
  versions: VersionEntry[];
  hasLastSaved: boolean;
  
  // PDF visibility toggle (mobile only)
  showPdfOnMobile?: boolean;
  onTogglePdfVisibility?: () => void;
  
  // PDF download
  pdfUrl?: string | null;
}

export type { ViewMode, VersionEntry };

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return `Today at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
}

function getEditingLabel(source: EditingSource, versions: VersionEntry[]): string {
  if (source.type === "current-draft") {
    return "Current Draft";
  }
  if (source.type === "default-template") {
    return "Default Template";
  }
  const version = versions.find((v) => v.id === source.versionId);
  return version?.name ?? "Unknown Version";
}

function getCompareLabel(source: CompareSource, versions: VersionEntry[]): string {
  if (source.type === "default-template") {
    return "Default Template";
  }
  if (source.type === "last-saved") {
    return "Last Saved";
  }
  const version = versions.find((v) => v.id === source.versionId);
  return version?.name ?? "Unknown Version";
}

export function TopBar({
  viewMode,
  onViewModeChange,
  isCompiling,
  compileError,
  hasValidationErrors,
  onCompile,
  onDownload,
  onSaveChanges,
  onSaveNewVersion,
  isSaving = false,
  editingSource,
  onEditingSourceChange,
  compareSource,
  onCompareSourceChange,
  selectedTemplate,
  onTemplateChange,
  availableTemplates,
  versions,
  hasLastSaved,
  showPdfOnMobile = true,
  onTogglePdfVisibility,
  pdfUrl,
}: TopBarProps) {
  const [editingOpen, setEditingOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const editingLabel = getEditingLabel(editingSource, versions);
  const compareLabel = getCompareLabel(compareSource, versions);

  // Handle PDF download - uses onDownload prop if provided, otherwise falls back to direct download
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      // Fallback: direct download without compilation
      const url = pdfUrl || "/pdf/resume-default.pdf";
      const link = document.createElement("a");
      link.href = url;
      link.download = "resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between min-h-14 sm:h-14 px-3 sm:px-5 py-2 sm:py-0 bg-zinc-900 border-b border-zinc-800 shadow-lg gap-2 sm:gap-0">
      {/* Top Row - Mobile: Logo + PDF/Compare Toggle, Desktop: Logo only */}
      <div className="flex items-center justify-between w-full sm:w-auto gap-2 sm:gap-2.5 shrink-0 pb-1 sm:pb-0 border-b sm:border-b-0 border-zinc-800">
        {/* Logo / Title */}
        <div className="flex items-center gap-2.5 shrink-0">
          <a href="/" className="cursor-pointer">
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
              <span className="text-white">my</span>
              <span className="text-emerald-500">sume</span>
            </h1>
          </a>
          <span className="text-[12px] font-medium text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded -mb-1">
            v{APP_VERSION}
          </span>
        </div>

        {/* View Mode Toggle - Mobile: Top Row (right side), Desktop: Hidden (shown in center) */}
        <div className="flex items-center sm:hidden">
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => onViewModeChange("pdf")}
              className={`flex items-center gap-2 h-9 px-4 rounded-md text-sm font-medium transition-all cursor-pointer ${
                viewMode === "pdf"
                  ? "bg-zinc-700 text-white shadow-sm"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <FileTextIcon className="size-4" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => onViewModeChange("diff")}
              className={`flex items-center gap-2 h-9 px-4 rounded-md text-sm font-medium transition-all cursor-pointer ${
                viewMode === "diff"
                  ? "bg-zinc-700 text-white shadow-sm"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <FileDiffIcon className="size-4" />
              <span>Compare</span>
            </button>
          </div>
        </div>
      </div>

      {/* Second Row - Mobile: Two Comboboxes, Desktop: Center Controls */}
      <div className="flex-1 sm:flex-initial flex items-center justify-center gap-2 sm:absolute sm:left-1/2 sm:-translate-x-1/2 w-full sm:w-auto sm:justify-center min-w-0 pb-1 sm:pb-0 border-b sm:border-b-0 border-zinc-800">
        {/* Editing Selector */}
        <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1 flex-1 sm:flex-initial min-w-0">
          <span className="hidden sm:inline text-sm text-zinc-500 font-medium ml-2">Editing:</span>
        <Popover open={editingOpen} onOpenChange={setEditingOpen}>
          <PopoverTrigger asChild>
            <button
              role="combobox"
              aria-expanded={editingOpen}
              className="flex items-center justify-between gap-2 h-7 px-2 sm:px-3 bg-zinc-700 text-zinc-200 hover:bg-zinc-600 hover:text-white text-xs sm:text-sm font-medium w-full sm:w-[140px] sm:max-w-[140px] rounded-md shadow-sm cursor-pointer transition-colors min-w-0"
            >
              <span className="flex items-center gap-2 truncate min-w-0 flex-1">
                {editingSource.type === "current-draft" && (
                  <span className="size-2 rounded-full bg-emerald-500 shrink-0" />
                )}
                <span className="truncate">{editingLabel}</span>
              </span>
              <ChevronsUpDownIcon className="size-3.5 shrink-0 text-zinc-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search versions..." />
              <CommandList>
                <CommandEmpty>No version found.</CommandEmpty>
                <CommandGroup heading="Current">
                  <CommandItem
                    value="Current Draft"
                    onSelect={() => {
                      onEditingSourceChange?.({ type: "current-draft" });
                      setEditingOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2 flex-1">
                      <span className="size-2 rounded-full bg-emerald-500 shrink-0" />
                      Current Draft
                    </span>
                    <CheckIcon
                      className={cn(
                        "size-4 shrink-0",
                        editingSource.type === "current-draft" ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup heading="Restore from">
                  <CommandItem
                    value="Default Template"
                    onSelect={() => {
                      onEditingSourceChange?.({ type: "default-template" });
                      setEditingOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2 flex-1">
                      <FileIcon className="size-3.5 text-zinc-400 shrink-0" />
                      Default Template
                    </span>
                    <CheckIcon
                      className={cn(
                        "size-4 shrink-0",
                        editingSource.type === "default-template" ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                </CommandGroup>
                {versions.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Saved Versions">
                      {versions.map((version) => (
                        <CommandItem
                          key={version.id}
                          value={version.name}
                          onSelect={() => {
                            onEditingSourceChange?.({ type: "version", versionId: version.id });
                            setEditingOpen(false);
                          }}
                        >
                          <span className="flex items-center gap-2 flex-1">
                            <HistoryIcon className="size-3.5 text-zinc-400 shrink-0" />
                            <span className="flex flex-col">
                              <span>{version.name}</span>
                              <span className="text-xs text-zinc-500">{formatTimestamp(version.timestamp)}</span>
                            </span>
                          </span>
                          <CheckIcon
                            className={cn(
                              "size-4 shrink-0",
                              editingSource.type === "version" && editingSource.versionId === version.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* View Mode Toggle - Desktop: shown in center, Mobile: hidden (shown in top row) */}
      <div className="hidden sm:flex items-center bg-zinc-800 rounded-lg p-1 flex-shrink-0 sm:w-[220px] sm:max-w-[220px]">
        <button
          onClick={() => onViewModeChange("pdf")}
          className={`flex items-center justify-center gap-2 h-7 px-3 rounded-md text-sm font-medium transition-all cursor-pointer flex-1 ${
            viewMode === "pdf"
              ? "bg-zinc-700 text-white shadow-sm"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <FileTextIcon className="size-3.5" />
          <span>PDF</span>
        </button>
        <button
          onClick={() => onViewModeChange("diff")}
          className={`flex items-center justify-center gap-2 h-7 px-3 rounded-md text-sm font-medium transition-all cursor-pointer flex-1 ${
            viewMode === "diff"
              ? "bg-zinc-700 text-white shadow-sm"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <FileDiffIcon className="size-3.5" />
          <span>Compare</span>
        </button>
      </div>

      {/* Template Selector - only visible in PDF mode */}
      {viewMode === "pdf" && (
        <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1 flex-1 sm:flex-initial min-w-0">
          <span className="hidden sm:inline text-sm text-zinc-500 font-medium ml-2">template</span>

          {/* Template selector */}
          <Popover open={templateOpen} onOpenChange={setTemplateOpen}>
            <PopoverTrigger asChild>
              <button
                role="combobox"
                aria-expanded={templateOpen}
                className="flex items-center justify-between gap-2 h-7 px-2 sm:px-3 bg-zinc-700 text-zinc-200 hover:bg-zinc-600 hover:text-white text-xs sm:text-sm font-medium w-full sm:w-[140px] sm:max-w-[140px] rounded-md shadow-sm cursor-pointer transition-colors min-w-0"
              >
                <span className="truncate flex-1 min-w-0">{selectedTemplate}</span>
                <ChevronsUpDownIcon className="size-3.5 shrink-0 text-zinc-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search templates..." />
                <CommandList>
                  <CommandEmpty>No template found.</CommandEmpty>
                  <CommandGroup heading="Templates">
                    {availableTemplates.map((template) => (
                      <CommandItem
                        key={template}
                        value={template}
                        onSelect={() => {
                          onTemplateChange?.(template);
                          setTemplateOpen(false);
                        }}
                      >
                        <span className="flex items-center gap-2 flex-1">
                          <FileIcon className="size-3.5 text-zinc-400 shrink-0" />
                          {template}
                        </span>
                        <CheckIcon
                          className={cn(
                            "size-4 shrink-0",
                            selectedTemplate === template ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Compare Selector - only visible in diff mode */}
      {viewMode === "diff" && (
        <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1 flex-1 sm:flex-initial min-w-0">
          <span className="hidden sm:inline text-sm text-zinc-500 font-medium ml-2">against</span>

          {/* Selectable right side - Compare against */}
          <Popover open={compareOpen} onOpenChange={setCompareOpen}>
            <PopoverTrigger asChild>
              <button
                role="combobox"
                aria-expanded={compareOpen}
                className="flex items-center justify-between gap-2 h-7 px-2 sm:px-3 bg-zinc-700 text-zinc-200 hover:bg-zinc-600 hover:text-white text-xs sm:text-sm font-medium w-full sm:w-[140px] sm:max-w-[140px] rounded-md shadow-sm cursor-pointer transition-colors min-w-0"
              >
                <span className="truncate flex-1 min-w-0">{compareLabel}</span>
                <ChevronsUpDownIcon className="size-3.5 shrink-0 text-zinc-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search..." />
                <CommandList>
                  <CommandEmpty>No option found.</CommandEmpty>
                  <CommandGroup heading="Reference">
                    <CommandItem
                      value="Default Template"
                      onSelect={() => {
                        onCompareSourceChange?.({ type: "default-template" });
                        setCompareOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-2 flex-1">
                        <FileIcon className="size-3.5 text-zinc-400 shrink-0" />
                        Default Template
                      </span>
                      <CheckIcon
                        className={cn(
                          "size-4 shrink-0",
                          compareSource.type === "default-template" ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                    <CommandItem
                      value="Last Saved"
                      disabled={!hasLastSaved}
                      onSelect={() => {
                        if (hasLastSaved) {
                          onCompareSourceChange?.({ type: "last-saved" });
                          setCompareOpen(false);
                        }
                      }}
                      className={!hasLastSaved ? "opacity-50" : ""}
                    >
                      <span className="flex items-center gap-2 flex-1">
                        <ClockIcon className="size-3.5 text-zinc-400 shrink-0" />
                        Last Saved
                        {!hasLastSaved && <span className="text-xs text-zinc-500">(no saves yet)</span>}
                      </span>
                      <CheckIcon
                        className={cn(
                          "size-4 shrink-0",
                          compareSource.type === "last-saved" ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  </CommandGroup>
                  {versions.length > 0 && (
                    <>
                      <CommandSeparator />
                      <CommandGroup heading="Saved Versions">
                        {versions.map((version) => (
                          <CommandItem
                            key={version.id}
                            value={version.name}
                            onSelect={() => {
                              onCompareSourceChange?.({ type: "version", versionId: version.id });
                              setCompareOpen(false);
                            }}
                          >
                            <span className="flex items-center gap-2 flex-1">
                              <HistoryIcon className="size-3.5 text-zinc-400 shrink-0" />
                              <span className="flex flex-col">
                                <span>{version.name}</span>
                                <span className="text-xs text-zinc-500">{formatTimestamp(version.timestamp)}</span>
                              </span>
                            </span>
                            <CheckIcon
                              className={cn(
                                "size-4 shrink-0",
                                compareSource.type === "version" && compareSource.versionId === version.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      </div>

      {/* Floating Action Buttons - Mobile only */}
      <div className="fixed bottom-6 right-6 sm:hidden z-50 flex flex-col gap-3 items-end">
        {/* PDF Visibility Toggle - only in PDF mode */}
        {viewMode === "pdf" && onTogglePdfVisibility && (
          <button
            onClick={onTogglePdfVisibility}
            className="flex items-center justify-center h-14 w-14 rounded-full shadow-lg transition-all bg-zinc-700 hover:bg-zinc-600 text-white cursor-pointer hover:scale-105"
            aria-label={showPdfOnMobile ? "Hide PDF" : "Show PDF"}
          >
            {showPdfOnMobile ? (
              <EyeOffIcon className="size-5" />
            ) : (
              <EyeIcon className="size-5" />
            )}
          </button>
        )}
        
        {/* Save New Version Button */}
        {onSaveNewVersion && (
          <button
            onClick={onSaveNewVersion}
            disabled={isSaving || hasValidationErrors}
            className={`flex items-center justify-center h-14 w-14 rounded-full shadow-lg transition-all ${
              isSaving || hasValidationErrors
                ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                : "bg-zinc-700 hover:bg-zinc-600 text-white cursor-pointer hover:scale-105"
            }`}
            aria-label="Save New Version"
          >
            {isSaving ? (
              <LoaderIcon className="size-5 animate-spin" />
            ) : (
              <GitBranchIcon className="size-5" />
            )}
          </button>
        )}
        
        {/* Save Button */}
        {onSaveChanges && (
          <button
            onClick={onSaveChanges}
            disabled={isSaving || hasValidationErrors}
            className={`flex items-center justify-center h-14 w-14 rounded-full shadow-lg transition-all ${
              isSaving || hasValidationErrors
                ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer hover:scale-105"
            }`}
            aria-label="Save"
          >
            {isSaving ? (
              <LoaderIcon className="size-5 animate-spin" />
            ) : (
              <SaveIcon className="size-5" />
            )}
          </button>
        )}
        
        {/* Download PDF Button - only in PDF mode */}
        {viewMode === "pdf" && (
          <button
            onClick={handleDownload}
            disabled={isCompiling || hasValidationErrors}
            className={`flex items-center justify-center h-14 w-14 rounded-full shadow-lg transition-all ${
              isCompiling || hasValidationErrors
                ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer hover:scale-105"
            }`}
            aria-label="Download PDF"
          >
            {isCompiling ? (
              <LoaderCircleIcon className="size-5 animate-spin" />
            ) : (
              <DownloadIcon className="size-5" />
            )}
          </button>
        )}
        
        {/* Refresh PDF Button - only in PDF mode */}
        {viewMode === "pdf" && (
          <div className="relative">
            <button
              onClick={onCompile}
              disabled={isCompiling || hasValidationErrors}
              className={`flex items-center justify-center h-14 w-14 rounded-full shadow-lg transition-all ${
                isCompiling || hasValidationErrors
                  ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                  : "bg-zinc-700 hover:bg-zinc-600 text-white cursor-pointer hover:scale-105"
              }`}
              aria-label="Refresh PDF"
            >
              {isCompiling ? (
                <LoaderCircleIcon className="size-5 animate-spin" />
              ) : (
                <RefreshCwIcon className="size-5" />
              )}
            </button>
            {/* Error indicator */}
            {compileError && (
              <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center border-2 border-zinc-900">
                <span className="text-[10px] text-white font-bold">!</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons - Desktop: visible here, Mobile: hidden (shown in third row) */}
      <div className="hidden sm:flex items-center gap-1.5 sm:gap-3 shrink-0">
        {compileError && viewMode === "pdf" && (
          <span
            className="text-xs text-red-400 max-w-48 truncate"
            title={compileError}
          >
            {compileError}
          </span>
        )}
        {/* Only show Download and Refresh PDF buttons in PDF mode */}
        {viewMode === "pdf" && (
          <>
            <button
              onClick={handleDownload}
              disabled={isCompiling || hasValidationErrors}
              className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all ${
                isCompiling || hasValidationErrors
                  ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm cursor-pointer"
              }`}
              aria-label="Download PDF"
            >
              {isCompiling ? (
                <LoaderCircleIcon className="size-4 animate-spin shrink-0" />
              ) : (
                <DownloadIcon className="size-4 shrink-0" />
              )}
            </button>
            <div className="relative">
              <button
                onClick={onCompile}
                disabled={isCompiling || hasValidationErrors}
                className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all ${
                  isCompiling || hasValidationErrors
                    ? "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                    : "bg-zinc-700 hover:bg-zinc-600 text-white shadow-sm cursor-pointer"
                }`}
                aria-label="Refresh PDF"
              >
                {isCompiling ? (
                  <LoaderCircleIcon className="size-4 animate-spin shrink-0" />
                ) : (
                  <RefreshCwIcon className="size-4 shrink-0" />
                )}
              </button>
              {/* Error indicator */}
              {compileError && (
                <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center border-2 border-zinc-900">
                  <span className="text-[8px] text-white font-bold">!</span>
                </div>
              )}
            </div>
          </>
        )}
        {onSaveChanges && (
          <ButtonGroup>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSaveChanges}
              disabled={isSaving || hasValidationErrors}
              className="h-auto py-1.5 px-2 sm:px-4 rounded-l-lg rounded-r-none bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white border border-emerald-600 hover:border-emerald-500 shadow-sm cursor-pointer focus-visible:ring-0 focus-visible:ring-offset-0 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:opacity-100 disabled:cursor-not-allowed disabled:border-zinc-600 text-xs sm:text-sm"
            >
              {isSaving ? (
                <LoaderIcon className="size-3 sm:size-3.5 animate-spin" />
              ) : (
                <SaveIcon className="size-3 sm:size-3.5" />
              )}
              <span className="hidden sm:inline">Save Changes</span>
              <span className="sm:hidden">Save</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isSaving || hasValidationErrors}
                  className="h-auto py-1.5 px-1.5 sm:px-2 rounded-r-lg rounded-l-none bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white border border-emerald-600 hover:border-emerald-500 border-l-emerald-800/50 shadow-sm cursor-pointer focus-visible:ring-0 focus-visible:ring-offset-0 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:opacity-100 disabled:cursor-not-allowed disabled:border-zinc-600"
                  aria-label="More save options"
                >
                  <ChevronDownIcon className="size-3 sm:size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700">
                <DropdownMenuItem
                  onSelect={() => onSaveNewVersion?.()}
                  className="text-zinc-200 focus:bg-zinc-700 focus:text-white cursor-pointer"
                >
                  <GitBranchIcon className="size-3.5" />
                  Save New Version
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        )}
      </div>
    </div>
  );
}

export default TopBar;
